/**
 * JSON Inheritance Support methods for LoaderPreProcess
 * Uses context stack for inheritance resolution (supports multi-parent scenarios)
 * These methods are attached to LoaderPreProcess.prototype in the main file
 */

import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonArray, JsonObject, JsonValue, JsonValueType } from '../../app/json/jsonValue.js';
import { JsonUtility } from '../../app/jsonUtility.js';
import { InheritanceOperation } from '../../model/index.js';
import type { LoaderPreProcess } from './loaderPreProcess.js';
import type { PhaseTiming } from '../../base/timing/phaseTiming.js';

/**
 * Inheritance methods to be attached to LoaderPreProcess prototype
 */
export const inheritanceMethods = {
    /**
     * Checks if GlobalData exists for this component (either globaldata or component-specific globalviewdata)
     * Uses SearchAppSites fallback to match ApplyGlobalData behavior
     */
    _hasGlobalData(this: LoaderPreProcess, appSite: string, componentName: string, resolvedAppSite: string | null): boolean {
        const targetAppSite = resolvedAppSite || appSite;
        const globalData = this._getGlobalDataWithFallback(targetAppSite, appSite);
        if (globalData !== null) return true;

        const globalViewData = this._getGlobalViewDataForComponentWithFallback(targetAppSite, appSite, componentName);
        if (globalViewData !== null) return true;

        return false;
    },

    /**
     * Gets parsed JSON with inheritance resolution and dynamic data processing
     * Resolves keys ending with # by searching up the context stack
     * Applies dynamic data handlers from Global/dynamicdata.json if configured
     * Uses context stack for inheritance resolution (supports multi-parent scenarios)
     * OPTIMIZATION: Only creates a new dictionary if inheritance keys exist or DynamicData handlers are configured
     */
    getTemplateDataWithInheritance(this: LoaderPreProcess, appSite: string, appView: string, appFile: string, jsonObj: JsonObject, resolvedTemplateName: string, timing: PhaseTiming, flowStack: string[] | null = null, resolvedAppSite: string | null = null, configJson: JsonObject | null = null, componentHtml: string | null = null, clientMode: boolean = false): JsonObject | null {
        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`GetTemplateJsonWithInheritance: appSite=${appSite}, resolvedTemplateName=${resolvedTemplateName}, flowStack=${flowStack ? flowStack.length : 0}`, 'LoaderPreProcess');
        }

        if (!jsonObj || !resolvedTemplateName) {
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`No JSON found for resolvedTemplateName=${resolvedTemplateName}`, 'LoaderPreProcess');
            }
            return null;
        }

        // OPTIMIZATION: First check if we have any inheritance keys (ending with #)
        let hasInheritanceKeys = false;
        for (const key of jsonObj.keys()) {
            if (key.endsWith('#')) {
                hasInheritanceKeys = true;
                break;
            }
        }

        // Determine which AppSite to use for DynamicData lookup
        const dynamicDataAppSite = this._config!.dynamicDataUseResolvedAppSite && resolvedAppSite
            ? resolvedAppSite
            : appSite;

        // Check if DynamicData handlers exist for this component
        const hasDynamicDataHandlers = this._hasDynamicDataHandlers(dynamicDataAppSite, resolvedTemplateName);

        // Check if DynamicAction handlers exist for this component (when actionMode is true)
        const hasDynamicActionHandlers = this._actionMode && this._hasDynamicActionHandlers(dynamicDataAppSite, resolvedTemplateName);

        // Check if GlobalData exists for this component (needs processing for placeholder replacement)
        const hasGlobalData = this._hasGlobalData(appSite, resolvedTemplateName, resolvedAppSite);

        // Check if a CLIENT-DATA preparer (dynamicclientdata.json) is bound — the Embedded island is produced by
        // applyDynamicClientDataHandlers below, so the fast-path must not short-circuit past it.
        const hasDynamicClientDataHandlers = this._hasDynamicClientDataHandlers(dynamicDataAppSite, resolvedTemplateName);

        // Build template key
        const templateKey = `${appSite.toLowerCase()}_${resolvedTemplateName.toLowerCase()}`;

        // CLIENT tier (clientMode) also runs the client-handler data-fill, so the fast-path must not short-circuit
        // past it. If no inheritance keys, no DynamicData handlers, no DynamicAction handlers, no GlobalData, no
        // client-data preparer, and not client mode, use original jsonObj directly
        if (!hasInheritanceKeys && !hasDynamicDataHandlers && !hasDynamicActionHandlers && !hasGlobalData && !hasDynamicClientDataHandlers && !clientMode) {
            const directJson = new JsonObject();
            for (const [key, value] of jsonObj.entries()) {
                directJson.set(key, value);
            }
            // Store in resolved cache for child inheritance (reference, not copy)
            this._dynamicResults.resolvedTemplateJson.set(templateKey, directJson);
            return directJson;
        }

        // JSON is already parsed - no need to parse again
        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            const rawKeys = Array.from(jsonObj.keys()).join(', ');
            Logger.debug(`Raw JSON keys for ${templateKey}: ${rawKeys}`, 'LoaderPreProcess');
        }

        let resolvedJson: JsonObject;
        if (hasInheritanceKeys) {
            resolvedJson = new JsonObject();

            // Process each JSON key
            for (const [key, jsonValue] of jsonObj.entries()) {
                // Get raw value from JsonValue if needed
                const value = (jsonValue instanceof JsonValue) ?
                    (jsonValue.typeName === JsonValueType.String ? jsonValue.asString() : null) :
                    (typeof jsonValue === 'string' ? jsonValue : null);
                const isStringValue = value !== null;

                // Check if this is an inheritable ARRAY key (ends with #, array value): the nearest parent array
                // wins wholesale, the locally declared array is the default (PLAN_TABLE_SCENARIO 8.1, Phase 3A)
                const arrayValue = (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.Array) ? jsonValue.asArray() : null;
                if (key.endsWith('#') && arrayValue !== null) {
                    const actualKey = key.substring(0, key.length - 1);
                    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                        Logger.debug(`Found inheritance array key: ${key}, defaultCount=${arrayValue.length}, resolving for actualKey=${actualKey}`, 'LoaderPreProcess');
                    }

                    const { resolvedArray, sourceTemplate, wasInherited } = this._resolveJsonArrayKeyWithInheritance(appSite, actualKey, arrayValue, templateKey, flowStack);
                    resolvedJson.set(actualKey, JsonValue.array(resolvedArray));

                    // Track inheritance operation for flow data (arrays serialized compact: the ops artifact is byte-compared across languages)
                    this._inheritanceOperations.push(new InheritanceOperation({
                        targetTemplate: resolvedTemplateName,
                        contextPath: flowStack ? [...flowStack] : [],
                        inheritKey: actualKey,
                        sourceTemplate: sourceTemplate || '',
                        defaultValue: JsonUtility.serializeObject(arrayValue),
                        resolvedValue: JsonUtility.serializeObject(resolvedArray),
                        wasInherited: wasInherited
                    }));

                    continue;
                }

                if (key.endsWith('#') && isStringValue) {
                    const actualKey = key.substring(0, key.length - 1);
                    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                        Logger.debug(`Found inheritance key: ${key}, defaultValue=${value}, resolving for actualKey=${actualKey}`, 'LoaderPreProcess');
                    }

                    const { resolvedValue, sourceTemplate, wasInherited } = this._resolveJsonKeyWithInheritance(appSite, actualKey, value!, templateKey, flowStack);
                    if (resolvedValue !== null) {
                        resolvedJson.set(actualKey, resolvedValue);
                        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                            Logger.debug(`Resolved inherited key ${key} -> ${actualKey} = ${resolvedValue}`, 'LoaderPreProcess');
                        }

                        // Track inheritance operation for flow data (capture full context path for tree building)
                        this._inheritanceOperations.push(new InheritanceOperation({
                            targetTemplate: resolvedTemplateName,
                            contextPath: flowStack ? [...flowStack] : [],
                            inheritKey: actualKey,
                            sourceTemplate: sourceTemplate || '',
                            defaultValue: value!,
                            resolvedValue: resolvedValue,
                            wasInherited: wasInherited
                        }));

                        continue;
                    }
                }

                // Normal key - keep as is (use original jsonValue)
                resolvedJson.set(key, jsonValue);
            }
        } else {
            // No inheritance keys - copy for potential dynamic data modification
            resolvedJson = new JsonObject();
            for (const [key, jsonValue] of jsonObj.entries()) {
                resolvedJson.set(key, jsonValue);
            }
        }

        // Apply global data after inheritance resolution, before DynamicData handlers
        this._applyGlobalData(appSite, resolvedTemplateName, resolvedJson, resolvedAppSite);

        // Apply dynamic data first, then action, then dynamic data again.
        // This matches the C# preprocessing contract for action scenarios.
        this.applyDynamicDataHandlers(dynamicDataAppSite, appView, appFile, resolvedTemplateName, 0, resolvedAppSite || dynamicDataAppSite, resolvedTemplateName, resolvedJson, timing, configJson, flowStack);

        const componentDepth = flowStack ? flowStack.length : 0;

        // Client-data preparer (dynamicclientdata.json): produce the RAW set for browser-side filtering into
        // dynamicClientDataResults. Client re-merges re-emit the delivered island below.
        if (!clientMode) {
            this.applyDynamicClientDataHandlers(dynamicDataAppSite, appView, appFile, resolvedTemplateName, componentDepth, resolvedAppSite || dynamicDataAppSite, resolvedTemplateName, resolvedJson, configJson, timing, flowStack);
        }

        // CLIENT tier consumption: fill resolvedJson from the delivered island via the registered client handler
        // (substitutes for the server data handler that ConfigClient does not ship) and re-emit the island at the
        // page tail — so the client re-merge matches the server render byte-for-byte.
        if (clientMode) {
            this.applyClientHandlers(dynamicDataAppSite, appView, appFile, resolvedTemplateName, componentDepth, resolvedAppSite || dynamicDataAppSite, resolvedTemplateName, componentHtml, resolvedJson, configJson, timing, flowStack);
        }

        if (this._actionMode) {
            this.applyDynamicActionHandlers(dynamicDataAppSite, appView, appFile, resolvedTemplateName, flowStack ? flowStack.length : 0, resolvedJson, resolvedAppSite || dynamicDataAppSite, resolvedTemplateName, timing, configJson, null, flowStack);
            JsonUtility.redactSensitiveInPlace(resolvedJson);
            const safeDataConfig = configJson === null ? null : JsonUtility.redactSensitive(configJson);
            this.applyDynamicDataHandlers(dynamicDataAppSite, appView, appFile, resolvedTemplateName, 0, resolvedAppSite || dynamicDataAppSite, resolvedTemplateName, resolvedJson, timing, safeDataConfig, flowStack);
        }

        // Store resolved JSON for child component inheritance (uses same key pattern as searchContextStackForKey)
        this._dynamicResults.resolvedTemplateJson.set(templateKey, resolvedJson);

        return resolvedJson;
    },

    /**
     * Searches up the context stack to find a JSON key value
     * Walks UP through context stack (from end toward beginning) to find inherited values
     */
    searchContextStackForKey(this: LoaderPreProcess, appSite: string, key: string, flowStack: string[]): { value: string | null; sourceTemplate: string | null } {
        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`SearchContextStackForKey: key=${key}, flowStack=[${flowStack.join(', ')}]`, 'LoaderPreProcess');
        }

        const appSiteLower = appSite.toLowerCase();
        const keyLower = key.toLowerCase();
        const cached = this._store.tryGetTemplates(appSite);

        // Walk UP through context stack (from end toward beginning)
        for (let i = flowStack.length - 1; i >= 0; i--) {
            const parentName = flowStack[i];
            const parentKey = `${appSiteLower}_${parentName.toLowerCase()}`;

            // FIRST: Check resolved template JSON (has DynamicData results)
            const resolvedJson = this._dynamicResults.resolvedTemplateJson.get(parentKey).asObject();
            if (resolvedJson) {
                for (const [jsonKey, jsonValue] of resolvedJson.entries()) {
                    if (jsonKey.toLowerCase() === keyLower) {
                        // Extract string value - handle both raw strings and JsonValue wrappers
                        let strValue: string | null = null;
                        if (typeof jsonValue === 'string') {
                            strValue = jsonValue;
                        } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                            strValue = jsonValue.asString();
                        }
                        if (strValue !== null) {
                            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                                Logger.debug(`Found key ${key} in resolved context at ${parentName}: ${strValue}`, 'LoaderPreProcess');
                            }
                            return { value: strValue, sourceTemplate: parentName };
                        }
                    }
                }
            }

            // SECOND: Fall back to original cache (static values from disk)
            if (cached) {
                let parentTemplate: { template: { originalContent: string }; json: JsonObject | null } | null = null;
                for (const [cacheName, cacheValue] of cached.entries()) {
                    const parts = cacheName.split('_');
                    if (parts.length >= 2) {
                        const templateName = parts.slice(1).join('_');
                        if (templateName.toLowerCase() === parentName.toLowerCase()) {
                            parentTemplate = cacheValue;
                            break;
                        }
                    }
                }

                if (parentTemplate && parentTemplate.json) {
                    for (const [jsonKey, jsonValue] of parentTemplate.json.entries()) {
                        if (jsonKey.toLowerCase() === keyLower) {
                            // Extract string value - handle both raw strings and JsonValue wrappers
                            let strValue: string | null = null;
                            if (typeof jsonValue === 'string') {
                                strValue = jsonValue;
                            } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                                strValue = jsonValue.asString();
                            }
                            if (strValue !== null) {
                                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                                    Logger.debug(`Found key ${key} in original cache at ${parentName}: ${strValue}`, 'LoaderPreProcess');
                                }
                                return { value: strValue, sourceTemplate: parentName };
                            }
                        }
                    }
                }
            }
        }

        // Not found in context stack
        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`Key ${key} not found in context stack`, 'LoaderPreProcess');
        }
        return { value: null, sourceTemplate: null };
    },

    /**
     * Searches up the context stack to find a JSON ARRAY key value (sibling of searchContextStackForKey)
     * Walks UP through context stack (from end toward beginning); resolved JSON first, then the original cache
     */
    searchContextStackForArrayKey(this: LoaderPreProcess, appSite: string, key: string, flowStack: string[]): { value: JsonArray | null; sourceTemplate: string | null } {
        const appSiteLower = appSite.toLowerCase();
        const keyLower = key.toLowerCase();
        const cached = this._store.tryGetTemplates(appSite);

        // Walk UP through context stack (from end toward beginning)
        for (let i = flowStack.length - 1; i >= 0; i--) {
            const parentName = flowStack[i];
            const parentKey = `${appSiteLower}_${parentName.toLowerCase()}`;

            // FIRST: Check resolved template JSON (has DynamicData results)
            const resolvedJson = this._dynamicResults.resolvedTemplateJson.get(parentKey).asObject();
            if (resolvedJson) {
                for (const [jsonKey, jsonValue] of resolvedJson.entries()) {
                    if (jsonKey.toLowerCase() === keyLower && jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.Array) {
                        return { value: jsonValue.asArray(), sourceTemplate: parentName };
                    }
                }
            }

            // SECOND: Fall back to original cache (static values from disk)
            if (cached) {
                let parentTemplate: { template: { originalContent: string }; json: JsonObject | null } | null = null;
                for (const [cacheName, cacheValue] of cached.entries()) {
                    const parts = cacheName.split('_');
                    if (parts.length >= 2) {
                        const templateName = parts.slice(1).join('_');
                        if (templateName.toLowerCase() === parentName.toLowerCase()) {
                            parentTemplate = cacheValue;
                            break;
                        }
                    }
                }

                if (parentTemplate && parentTemplate.json) {
                    for (const [jsonKey, jsonValue] of parentTemplate.json.entries()) {
                        if (jsonKey.toLowerCase() === keyLower && jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.Array) {
                            return { value: jsonValue.asArray(), sourceTemplate: parentName };
                        }
                    }
                }
            }
        }

        // Not found in context stack
        return { value: null, sourceTemplate: null };
    },

    /**
     * Resolves a JSON key by searching up the context stack
     * Uses context stack for inheritance resolution (supports multi-parent scenarios)
     */
    _resolveJsonKeyWithInheritance(this: LoaderPreProcess, appSite: string, actualKey: string, defaultValue: string, currentTemplateKey: string, flowStack: string[] | null = null): { resolvedValue: string | null; sourceTemplate: string | null; wasInherited: boolean } {
        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`Resolving inherited key: ${actualKey} for template ${currentTemplateKey}, flowStack=${flowStack ? flowStack.length : 0}`, 'LoaderPreProcess');
        }

        // Use context stack for inheritance resolution (supports multi-parent scenarios)
        if (flowStack && flowStack.length > 0) {
            const { value: inheritedValue, sourceTemplate } = this.searchContextStackForKey(appSite, actualKey, flowStack);
            if (inheritedValue !== null) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`Found inherited value for ${actualKey}: ${inheritedValue} from ${sourceTemplate}`, 'LoaderPreProcess');
                }
                return { resolvedValue: inheritedValue, sourceTemplate, wasInherited: true };
            }
        }

        // If not found in context stack, use the default value
        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`No inherited value found for ${actualKey}, using default: ${defaultValue}`, 'LoaderPreProcess');
        }
        return { resolvedValue: defaultValue, sourceTemplate: null, wasInherited: false };
    },

    /**
     * Resolves a JSON ARRAY key by searching up the context stack (sibling of _resolveJsonKeyWithInheritance)
     * The nearest parent array wins wholesale; the locally declared array is the default
     */
    _resolveJsonArrayKeyWithInheritance(this: LoaderPreProcess, appSite: string, actualKey: string, defaultArray: JsonArray, currentTemplateKey: string, flowStack: string[] | null = null): { resolvedArray: JsonArray; sourceTemplate: string | null; wasInherited: boolean } {
        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`Resolving inherited array key: ${actualKey} for template ${currentTemplateKey}, flowStack=${flowStack ? flowStack.length : 0}`, 'LoaderPreProcess');
        }

        // Use context stack for inheritance resolution (supports multi-parent scenarios)
        if (flowStack && flowStack.length > 0) {
            const { value: inheritedArray, sourceTemplate } = this.searchContextStackForArrayKey(appSite, actualKey, flowStack);
            if (inheritedArray !== null) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`Found inherited array for ${actualKey}: ${inheritedArray.length} items from ${sourceTemplate}`, 'LoaderPreProcess');
                }
                return { resolvedArray: inheritedArray, sourceTemplate, wasInherited: true };
            }
        }

        // If not found in context stack, use the default array
        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`No inherited array found for ${actualKey}, using default (${defaultArray.length} items)`, 'LoaderPreProcess');
        }
        return { resolvedArray: defaultArray, sourceTemplate: null, wasInherited: false };
    },

    // #region Global Data (Private)

    /**
     * Applies globaldata and globalviewdata values to component JSON
     * Precedence: Component JSON < globaldata < globalviewdata (component-specific)
     * Placeholders like {{GlobalKey}} are replaced with actual values
     */
    _applyGlobalData(this: LoaderPreProcess, appSite: string, componentName: string, componentJson: JsonObject, resolvedAppSite: string | null): void {
        const targetAppSite = resolvedAppSite || appSite;

        // 1. Get globaldata (lowest global precedence) with SearchAppSites fallback
        const globalData = this._getGlobalDataWithFallback(targetAppSite, appSite);

        // 2. Get globalviewdata (component-specific, higher precedence) with SearchAppSites fallback
        const globalViewData = this._getGlobalViewDataForComponentWithFallback(targetAppSite, appSite, componentName);

        // 3. Merge in order: globaldata first, then globalviewdata
        this._mergeGlobalDataIntoComponent(componentJson, globalData);
        this._mergeGlobalDataIntoComponent(componentJson, globalViewData);
    },

    /**
     * Shared fallback+merge logic for global data retrieval
     */
    _getWithFallback(this: LoaderPreProcess, targetAppSite: string, appSite: string, getter: (site: string) => JsonObject | null): JsonObject | null {
        // Non-merge mode: return first found
        if (!this._config!.globalDataMergeFromFallback) {
            let result = getter(targetAppSite);
            if (result !== null) return result;

            if (targetAppSite !== appSite) {
                result = getter(appSite);
                if (result !== null) return result;
            }

            if (typeof this._config!.getSearchAppSitesForAppSite !== 'function') {
                return null;
            }
            const searchAppSites = this._config!.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites && searchAppSites.length > 0) {
                const searches = searchAppSites.split(',');
                for (let i = 0; i < searches.length; i++) {
                    const trimmed = searches[i].trim();
                    if (!trimmed) continue;
                    result = getter(trimmed);
                    if (result !== null) return result;
                }
            }
            return null;
        }

        // Merge mode: collect all sources and merge
        const sources: JsonObject[] = [];

        const targetData = getter(targetAppSite);
        if (targetData !== null) {
            sources.push(targetData);
        }

        if (targetAppSite !== appSite) {
            const appSiteData = getter(appSite);
            if (appSiteData !== null) {
                sources.push(appSiteData);
            }
        }

        if (typeof this._config!.getSearchAppSitesForAppSite === 'function') {
            const searchAppSites = this._config!.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites && searchAppSites.length > 0) {
                const searches = searchAppSites.split(',');
                for (let i = 0; i < searches.length; i++) {
                    const trimmed = searches[i].trim();
                    if (!trimmed) continue;
                    const searchData = getter(trimmed);
                    if (searchData !== null) {
                        sources.push(searchData);
                    }
                }
            }
        }

        if (sources.length === 0) return null;
        if (sources.length === 1) return sources[0];

        // Merge all sources
        const merged = new JsonObject();
        const overwrite = this._config!.globalDataMergeOverwrite;
        for (let i = 0; i < sources.length; i++) {
            for (const [key, value] of sources[i].entries()) {
                if (!merged.has(key) || overwrite) {
                    merged.set(key, value as JsonValue);
                }
            }
        }
        return merged;
    },

    _getGlobalDataWithFallback(this: LoaderPreProcess, targetAppSite: string, appSite: string): JsonObject | null {
        // Client mode: use stored globalData from API response
        const gd = (this as unknown as { _globalData?: Map<string, JsonObject> })._globalData;
        if (gd) {
            if (gd.has(targetAppSite)) {
                return gd.get(targetAppSite) ?? null;
            }
            if (gd.has(appSite)) {
                return gd.get(appSite) ?? null;
            }
        }

        return this._getWithFallback(targetAppSite, appSite, (site: string) => this.getGlobalData(site));
    },

    _getGlobalViewDataForComponentWithFallback(this: LoaderPreProcess, targetAppSite: string, appSite: string, componentName: string): JsonObject | null {
        // Client mode: use stored globalViewData from API response
        // GlobalViewData contains component-specific mappings: {ComponentName: {key: value}}
        const componentLower = componentName.toLowerCase();
        if ((this as unknown as { _globalViewData?: Map<string, JsonObject> })._globalViewData) {
            const gvd = (this as unknown as { _globalViewData: Map<string, JsonObject> })._globalViewData;
            for (const [, viewData] of gvd.entries()) {
                if (viewData instanceof JsonObject) {
                    for (const key of viewData.keys()) {
                        if (key.toLowerCase() === componentLower) {
                            const val = viewData.get(key);
                            return val.isObject ? val.asObject() : null;
                        }
                    }
                }
            }
        }

        return this._getWithFallback(targetAppSite, appSite, (site: string) => this.getGlobalViewDataForComponent(site, componentName));
    },

    /**
     * Case-insensitive key lookup in a JsonObject (Map-based)
     * Returns the actual key if found (preserving original casing), or null if not found
     */
    _findKeyIgnoreCase(this: LoaderPreProcess, data: JsonObject, key: string): string | null {
        if (data.has(key)) return key;
        const lowerKey = key.toLowerCase();
        for (const k of data.keys()) {
            if (k.toLowerCase() === lowerKey) return k;
        }
        return null;
    },

    /**
     * Merges global data into component JSON
     * Handles {{placeholder}} pattern replacement and direct key overwrites
     */
    _mergeGlobalDataIntoComponent(this: LoaderPreProcess, componentJson: JsonObject, globalData: JsonObject | null): void {
        if (globalData === null) {
            return;
        }

        if (globalData.size === 0) {
            return;
        }

        // Helper functions for JsonObject (Map-based)
        const getValue = (key: string): unknown => globalData.get(key);
        const findKeyIgnoreCase = (key: string): string | null => this._findKeyIgnoreCase(globalData, key);

        const keysToProcess = Array.from(componentJson.keys());
        for (let i = 0; i < keysToProcess.length; i++) {
            const key = keysToProcess[i];
            const rawValue = componentJson.get(key);

            // Extract string value - handle both raw strings and JsonValue wrappers
            let strValue: string | null = null;
            if (typeof rawValue === 'string') {
                strValue = rawValue;
            } else if (rawValue instanceof JsonValue && rawValue.typeName === JsonValueType.String) {
                strValue = rawValue.asString();
            }

            if (strValue !== null) {
                // Check for {{placeholder}} pattern
                if (strValue.startsWith('{{') && strValue.endsWith('}}')) {
                    const placeholderKey = strValue.substring(2, strValue.length - 2);
                    const resolvedKey = findKeyIgnoreCase(placeholderKey);
                    if (resolvedKey !== null) {
                        componentJson.set(key, getValue(resolvedKey));
                    }
                } else if (strValue.includes('{{')) {
                    let result = strValue;
                    let startIdx = 0;
                    while (true) {
                        const pos = result.indexOf('{{', startIdx);
                        if (pos < 0) break;
                        const endPos = result.indexOf('}}', pos + 2);
                        if (endPos < 0) break;
                        const placeholderKey = result.substring(pos + 2, endPos);
                        const resolvedInlineKey = findKeyIgnoreCase(placeholderKey);
                        if (resolvedInlineKey !== null) {
                            const globalValue = getValue(resolvedInlineKey);
                            let replacement: string | null = null;
                            if (typeof globalValue === 'string') {
                                replacement = globalValue;
                            } else if (globalValue instanceof JsonValue && globalValue.typeName === JsonValueType.String) {
                                replacement = globalValue.asString();
                            }
                            if (replacement !== null) {
                                result = result.substring(0, pos) + replacement + result.substring(endPos + 2);
                                startIdx = pos + replacement.length;
                                continue;
                            }
                        }
                        startIdx = endPos + 2;
                    }
                    if (result !== strValue) {
                        componentJson.set(key, new JsonValue(JsonValueType.String, result));
                    }
                }
            }
            else {
                // Array append/skip: if component has an array and globalData has the same key as array,
                // append items from globalData that don't already exist in the component array
                let isArray = false;
                if (rawValue instanceof JsonValue && rawValue.typeName === JsonValueType.Array) {
                    isArray = true;
                } else if (Array.isArray(rawValue)) {
                    isArray = true;
                }
                const resolvedArrayKey = findKeyIgnoreCase(key);
                if (isArray && resolvedArrayKey !== null) {
                    const globalValue = getValue(resolvedArrayKey);
                    let globalIsArray = false;
                    if (globalValue instanceof JsonValue && globalValue.typeName === JsonValueType.Array) {
                        globalIsArray = true;
                    } else if (Array.isArray(globalValue)) {
                        globalIsArray = true;
                    }
                    if (globalIsArray) {
                        // Get arrays
                        let componentArray: unknown[] | null = null;
                        if (rawValue instanceof JsonValue) {
                            componentArray = rawValue.asArray() as unknown as unknown[];
                        } else {
                            componentArray = rawValue as unknown[];
                        }
                        let globalArray: unknown[] | null = null;
                        if (globalValue instanceof JsonValue) {
                            globalArray = (globalValue as JsonValue).asArray() as unknown as unknown[];
                        } else {
                            globalArray = globalValue as unknown[];
                        }

                        // Check for optional ArrayNamePrimary
                        const primaryKeyName = key + 'Primary';
                        let primaryKey: string | null = null;
                        if (componentJson.has(primaryKeyName)) {
                            const pkValue = componentJson.get(primaryKeyName);
                            if (pkValue !== null && pkValue !== undefined) {
                                const pkStr = (pkValue instanceof JsonValue) ? pkValue.asString() : (typeof pkValue === 'string' ? pkValue : null);
                                if (pkStr) primaryKey = pkStr;
                            }
                        }

                        // Build new array to avoid mutating shared cached data
                        if (componentArray && globalArray) {
                            const newArray = componentArray.slice();
                            for (let g = 0; g < globalArray.length; g++) {
                                const globalItem = globalArray[g];
                                const globalObj = (globalItem instanceof JsonValue) ? (globalItem as JsonValue).asObject() : (globalItem instanceof JsonObject ? globalItem : null);
                                if (!globalObj) continue;

                                let existsInComponent = false;
                                for (let c = 0; c < newArray.length; c++) {
                                    const compItem = newArray[c];
                                    const compObj = (compItem instanceof JsonValue) ? (compItem as JsonValue).asObject() : (compItem instanceof JsonObject ? compItem : null);
                                    if (!compObj) continue;

                                    if (this._matchesByStringKeys(compObj, globalObj, primaryKey)) {
                                        existsInComponent = true;
                                        break;
                                    }
                                }

                                if (!existsInComponent) {
                                    newArray.push(globalItem);
                                }
                            }
                            // Assign new array back, preserving JsonValue wrapper if original was wrapped
                            if (rawValue instanceof JsonValue) {
                                componentJson.set(key, new JsonValue(JsonValueType.Array, newArray as unknown as JsonArray));
                            } else {
                                componentJson.set(key, newArray);
                            }
                        }
                    }
                }
            }
        }
    },

    /**
     * Gets a string representation of a scalar JsonValue (string, integer, number, bool)
     * Returns null for non-scalar types (array, object)
     */
    _getScalarString(this: LoaderPreProcess, val: JsonValue): string | null {
        return _getScalarString(val);
    },

    _matchesByStringKeys(this: LoaderPreProcess, componentItem: JsonObject, globalItem: JsonObject, primaryKey: string | null): boolean {
        if (primaryKey !== null) {
            if (!componentItem.containsKey(primaryKey) || !globalItem.containsKey(primaryKey)) return false;
            const compStr = _getScalarString(componentItem.get(primaryKey));
            const globalStr = _getScalarString(globalItem.get(primaryKey));
            return compStr !== null && globalStr !== null && compStr === globalStr;
        }

        let hasScalarKey = false;
        for (const [key, value] of globalItem.entries()) {
            const globalStr = _getScalarString(value);
            if (globalStr === null) continue;
            hasScalarKey = true;
            if (!componentItem.containsKey(key)) return false;
            const compStr = _getScalarString(componentItem.get(key));
            if (compStr === null || compStr !== globalStr) return false;
        }
        return hasScalarKey;
    },

    // #endregion
};

function _getScalarString(val: JsonValue): string | null {
    if (val.typeName === JsonValueType.String) return val.asString();
    if (val.typeName === JsonValueType.Integer) return val.asInteger()?.toString() ?? null;
    if (val.typeName === JsonValueType.Number) return val.asNumber()?.toString() ?? null;
    if (val.typeName === JsonValueType.Bool) return val.asBool() === true ? 'true' : 'false';
    return null;
}
